import React from 'react';
import { Link } from 'react-router-dom';
import { useHemisphere } from '@/context/HemisphereContext';
import NewsletterSignup from '@/components/newsletter/NewsletterSignup';
import type { Hemisphere } from '@/lib/hemisphere';
import './Footer.css';

/**
 * Sets the seasons used across the whole site. Sowing months, monthly tips and
 * the ascending/descending moon all follow it.
 */
function HemisphereSwitcher() {
  const { hemisphere, setHemisphere } = useHemisphere();

  const options: { value: Hemisphere; label: string; emoji: string }[] = [
    { value: 'northern', label: 'Northern', emoji: '🌍' },
    { value: 'southern', label: 'Southern', emoji: '🌏' },
  ];

  return (
    <div className="footer-hemisphere">
      <span className="footer-hemisphere-label text-caption">Growing seasons</span>
      <div className="footer-hemisphere-options" role="group" aria-label="Hemisphere">
        {options.map(o => (
          <button
            key={o.value}
            type="button"
            className={`footer-hemisphere-btn ${hemisphere === o.value ? 'footer-hemisphere-btn--active' : ''}`}
            aria-pressed={hemisphere === o.value}
            onClick={() => setHemisphere(o.value)}
          >
            {o.emoji} {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="site-footer">
      {/* Botanical border */}
      <div className="footer-border" aria-hidden="true">
        <FooterBorderSvg />
      </div>

      <div className="footer-inner container">
        {/* Its own strip — the footer grid has no room for a fifth column */}
        <div className="footer-newsletter">
          <NewsletterSignup source="footer" variant="compact" />
        </div>

        <div className="footer-grid">
          {/* Brand */}
          <div className="footer-brand">
            <div className="footer-logo-wrap">
              <span className="footer-logo-text text-headline">The Gardeners Calendar</span>
            </div>
            <p className="footer-tagline text-subtitle">
              Growing with nature, season by season.
            </p>
            <p className="footer-about text-small">
              An organic and biodynamic gardening companion for home gardeners, allotment holders, and small farmers. Celebrating traditional knowledge and the wisdom of growing in harmony with the land.
            </p>
          </div>

          {/* Explore */}
          <div className="footer-col">
            <h3 className="footer-col-title text-label">Explore</h3>
            <ul className="footer-links">
              <li><Link to="/plants">Plant Database</Link></li>
              <li><Link to="/biodynamic">Biodynamic Growing</Link></li>
              <li><Link to="/advice">Gardening Advice</Link></li>
              <li><Link to="/directory">Directory</Link></li>
            </ul>
          </div>

          {/* My Garden */}
          <div className="footer-col">
            <h3 className="footer-col-title text-label">My Garden</h3>
            <ul className="footer-links">
              <li><Link to="/my-garden">My Garden</Link></li>
              <li><Link to="/my-garden/diary">Garden Diary</Link></li>
              <li><Link to="/my-garden/plants">My Plants</Link></li>
              <li><Link to="/dashboard">Dashboard</Link></li>
            </ul>
          </div>

          {/* Info */}
          <div className="footer-col">
            <h3 className="footer-col-title text-label">Information</h3>
            <ul className="footer-links">
              <li><Link to="/about">About</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Use</Link></li>
              <li><a href="mailto:hello@gardenerscalendar.com">Contact</a></li>
            </ul>
          </div>
        </div>

        {/* Seasonal quote */}
        <blockquote className="footer-quote">
          <p>"The glory of gardening: hands in the dirt, head in the sun, heart with nature. To nurture a garden is to feed not just the body, but the soul."</p>
          <cite>Alfred Austin</cite>
        </blockquote>

        <HemisphereSwitcher />

        <div className="footer-bottom">
          <p className="footer-copy text-caption">
            &copy; {year} The Gardeners Calendar. Built with care for the land.
          </p>
          <p className="footer-organic text-caption">
            <span>🌱</span> Promoting organic and biodynamic growing practices
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterBorderSvg() {
  return (
    <svg viewBox="0 0 1440 40" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" className="footer-border-svg" aria-hidden="true">
      {/* Wavy botanical border with leaves */}
      <path d="M0 20 C60 10 120 30 180 20 C240 10 300 30 360 20 C420 10 480 30 540 20 C600 10 660 30 720 20 C780 10 840 30 900 20 C960 10 1020 30 1080 20 C1140 10 1200 30 1260 20 C1320 10 1380 30 1440 20 L1440 40 L0 40 Z"
        fill="var(--sage-wash)" />
      {/* Small leaf shapes along the wave */}
      {[90, 270, 450, 630, 810, 990, 1170, 1350].map(x => (
        <g key={x} transform={`translate(${x}, 18)`}>
          <ellipse cx="0" cy="-3" rx="5" ry="3" fill="var(--sage-light)" opacity="0.6" transform="rotate(-30)"/>
          <ellipse cx="6" cy="-2" rx="5" ry="3" fill="var(--sage-pale)" opacity="0.5" transform="rotate(20)"/>
        </g>
      ))}
    </svg>
  );
}
